import { faXmark } from "@fortawesome/pro-regular-svg-icons";
import {
  Button,
  Drawer,
  Grid,
  Icon,
  Label,
  Message,
  Modal,
} from "@intility/bifrost-react";
import { useState } from "react";
import {
  useDisableLocalClient,
  useEnableLocalClient,
  useLocalClient,
} from "../api/clients";
import type { ClientListItem } from "../api/types";
import { SecretField } from "./ClientSecret";
import { CodeRow } from "./ClientWorkloadIdentity";

// Local development for a SPIFFE client (ADR-0046): a `<clientId>-local`
// companion that authenticates with a client secret, so the client can be
// developed on a laptop, which has no SPIFFE identity. It is part of this
// client, never a second one: it resolves to the same principal.
export function ClientLocalDevDrawer({
  client,
  open,
  onClose,
}: {
  client: ClientListItem;
  open: boolean;
  onClose: () => void;
}) {
  const local = useLocalClient(client.id);
  const enable = useEnableLocalClient(client.id);
  const disable = useDisableLocalClient(client.id);
  const [confirmDisable, setConfirmDisable] = useState(false);

  const enabled = !!local.data?.enabled;
  const companionId = local.data?.id ?? "";

  async function disableCompanion() {
    try {
      await disable.mutateAsync();
      setConfirmDisable(false);
    } catch {
      // The error shows in the dialog.
    }
  }

  function closeDisable() {
    if (disable.isPending) return;
    setConfirmDisable(false);
    disable.reset();
  }

  return (
    <>
      <Drawer
        isOpen={open}
        onRequestClose={onClose}
        header="Local development"
        footer={
          <div style={{ display: "flex", gap: 12, justifyContent: "flex-end" }}>
            <Button variant="flat" onClick={onClose}>
              <Icon icon={faXmark} marginRight />
              Close
            </Button>
            {local.data &&
              (enabled ? (
                <Button
                  variant="basic"
                  state="alert"
                  onClick={() => setConfirmDisable(true)}
                >
                  Disable
                </Button>
              ) : (
                <Button
                  variant="filled"
                  state={enable.isPending ? "inactive" : undefined}
                  onClick={enable.isPending ? undefined : () => enable.mutate()}
                >
                  {enable.isPending ? "Enabling…" : "Enable local development"}
                </Button>
              ))}
          </div>
        }
      >
        <Grid gap={16} style={{ marginTop: 24 }}>
          {local.isLoading && (
            <p className="bf-p bfc-base-2" style={{ margin: 0 }}>
              Loading…
            </p>
          )}
          {local.isError && (
            <Message state="warning" header="Failed to load">
              {local.error instanceof Error
                ? local.error.message
                : "An unexpected error occurred."}
            </Message>
          )}

          {local.data && !enabled && (
            <p className="bf-p bfc-base-2" style={{ margin: 0 }}>
              Enable a <code>{client.clientId}-local</code> companion that
              authenticates with a client secret, for developing this client on
              a laptop against the dev environment. It resolves to the same
              principal as this client.
            </p>
          )}

          {local.data && enabled && (
            <>
              <p className="bf-p bfc-base-2" style={{ margin: 0 }}>
                This client has a local development companion. It authenticates
                with a client secret and resolves to the same principal as this
                client. Use its client ID and secret in your local configuration
                instead of a SPIFFE credential.
              </p>
              {local.data.clientId && (
                <div>
                  <Label>Client ID</Label>
                  <CodeRow value={local.data.clientId} />
                </div>
              )}
              {companionId && (
                <div>
                  <Label>Client secret</Label>
                  <SecretField
                    key={companionId}
                    secretClientId={companionId}
                    name={local.data.clientId ?? client.name}
                  />
                </div>
              )}
            </>
          )}

          {enable.error && (
            <Message state="warning" header="Enable failed">
              {enable.error instanceof Error
                ? enable.error.message
                : "An unexpected error occurred."}
            </Message>
          )}
        </Grid>
      </Drawer>

      <Modal
        isOpen={confirmDisable}
        onRequestClose={closeDisable}
        header="Disable local development"
        width={480}
      >
        <Grid gap={16}>
          <p className="bf-p" style={{ margin: 0 }}>
            This deletes the <code>{client.clientId}-local</code> companion and
            its secret. Any local configuration using it stops authenticating
            immediately. This cannot be undone.
          </p>
          {disable.error && (
            <Message state="alert" header="Disable failed">
              {disable.error instanceof Error
                ? disable.error.message
                : "Something went wrong."}
            </Message>
          )}
          <div style={{ display: "flex", gap: 12, justifyContent: "flex-end" }}>
            <Button
              variant="flat"
              onClick={closeDisable}
              state={disable.isPending ? "inactive" : undefined}
            >
              Cancel
            </Button>
            <Button
              variant="filled"
              state={disable.isPending ? "inactive" : "alert"}
              onClick={disable.isPending ? undefined : disableCompanion}
            >
              {disable.isPending ? "Disabling…" : "Disable"}
            </Button>
          </div>
        </Grid>
      </Modal>
    </>
  );
}
